import axiosNest from '../../api/axiosNest';

/**
 * Czasy załadunku i rozładunku według miejsca (08.10.2026, ClickUp
 * 869fd2g52) — zestawienie z `/stop-times` w API, przeliczenie przez
 * `/stop-times-schedule`. Progi, wartości domyślne i liczby pomiarów trzyma
 * API — front pokazuje to, co dostał.
 */

export type StopScope = 'location' | 'contractor';
export type StopKind = 'loading' | 'unloading';

export type StopTimeRow = {
  id: number;
  scope: StopScope;
  key: string;
  kind: StopKind;
  observations: number;
  medianH: number;
  p25H: number;
  p75H: number;
  p90H: number;
  periodFrom: string;
  periodTo: string;
  computedAt: string;
  countryCode: string | null;
  city: string | null;
  zipCode: string | null;
  cellLat: number | null;
  cellLng: number | null;
  contractorId: number | null;
  contractorName: string | null;
  /** Czy reguły biorą tę medianę (pomiarów co najmniej `threshold`). */
  inUse: boolean;
  threshold: number;
  /**
   * Dlaczego reguły jej nie biorą (19.10.2026, przełącznik
   * `stopTimesReliablePlace`): za mało postojów, za duży rozrzut albo
   * zleceniodawca nieużywany; `null`/brak — sam próg liczby pomiarów.
   */
  notUsedReason?: string | null;
};

export type StopTimeRun = {
  id: number;
  triggeredBy: string;
  triggeredByName: string | null;
  startedAt: string;
  finishedAt: string | null;
  examined: number;
  accepted: number;
  rejected: Record<string, number> | null;
  locations: number;
  contractors: number;
  note: string | null;
  error: string | null;
};

export type StopTimesSummary = {
  defaults: { loadingH: number; unloadingH: number };
  /**
   * Reguła obowiązująca dla konta (19.10.2026): `place` — miejsce, potem
   * zleceniodawca; `reliablePlace` — tylko miarodajne miejsca (próg rozrzutu
   * w `maxSpreadPct`, zleceniodawcy nieużywani: `contractor` = `null`).
   */
  rule?: 'fleet' | 'place' | 'reliablePlace';
  thresholds: { location: number; contractor: number | null; maxSpreadPct?: number | null };
  aggregateDays: number;
  observations: Array<{ status: string; label: string; count: number }>;
  runs: StopTimeRun[];
};

export type StopTimesSchedule = {
  enabled: boolean;
  hour: number;
  lastStartedAt: string | null;
  running: boolean;
  lastResult: string | null;
  errors: string[];
};

export type StopTimeSort = 'medianH' | 'observations' | 'p75H' | 'computedAt' | 'countryCode' | 'city';

export type StopTimeQuery = {
  scope: StopScope;
  kind?: StopKind | '';
  q?: string;
  country?: string;
  inUse?: boolean;
  sort: StopTimeSort;
  order: 'asc' | 'desc';
  page: number;
  perPage: number;
};

/** Zapytanie bez pustych pól — API dostaje tylko ustawione filtry. */
export function stopTimeParams(q: StopTimeQuery): Record<string, string | number> {
  const params: Record<string, string | number> = {
    scope: q.scope,
    sort: q.sort,
    order: q.order,
    page: q.page,
    perPage: q.perPage,
  };
  if (q.kind) params.kind = q.kind;
  if (q.q && q.q.trim() !== '') params.q = q.q.trim();
  if (q.country && q.country.trim() !== '') params.country = q.country.trim().toUpperCase();
  if (q.inUse) params.inUse = 'true';
  return params;
}

export async function fetchStopTimes(q: StopTimeQuery): Promise<{ rows: StopTimeRow[]; total: number }> {
  const response = await axiosNest.get<StopTimeRow[]>('/stop-times', { params: stopTimeParams(q) });
  const header: string | undefined = response.headers?.['content-range'];
  const total = header ? Number(header.split('/')[1]) : response.data.length;
  return { rows: response.data, total: Number.isFinite(total) ? total : 0 };
}

export async function fetchStopTimesSummary(): Promise<StopTimesSummary> {
  const { data } = await axiosNest.get<StopTimesSummary>('/stop-times/summary');
  return data;
}

export async function fetchStopTimesSchedule(): Promise<StopTimesSchedule> {
  const { data } = await axiosNest.get<StopTimesSchedule>('/stop-times-schedule');
  return data;
}

/** Ręczne przeliczenie — sekcja administracyjna; drugie w trakcie = 409. */
export async function runStopTimes(): Promise<{ examined: number; accepted: number }> {
  const { data } = await axiosNest.post('/stop-times-schedule/run');
  return data;
}

/** „3,1 h” — godziny z przecinkiem, jedno miejsce. */
export const hoursPl = (h: number): string =>
  `${h.toLocaleString('pl-PL', { maximumFractionDigits: 1 })} h`;

export const KIND_LABEL: Record<StopKind, string> = {
  loading: 'Załadunek',
  unloading: 'Rozładunek',
};

/** Miejsce jednym napisem: „PL 00-001 Warszawa”; bez etykiety — współrzędne komórki. */
export function placeLabel(r: Pick<StopTimeRow, 'scope' | 'countryCode' | 'zipCode' | 'city' | 'key' | 'contractorName' | 'contractorId'>): string {
  if (r.scope === 'contractor') {
    return r.contractorName ?? `kontrahent #${r.contractorId ?? r.key}`;
  }
  const label = [r.countryCode, r.zipCode, r.city].filter(Boolean).join(' ');
  return label === '' ? `komórka ${r.key}` : label;
}
